/*
 * Geisti PDP: one option click lands on a real variant in ONE update.
 *
 * Geisti products never track inventory, so a combination either exists or
 * isn't sold. Switching Medium can leave the checked Frame or Size pointing at
 * a combination the new Medium doesn't have. Instead of letting Horizon fetch
 * that dead combination and correcting it afterwards (two fetches, a blank
 * price and an all-images gallery in between), a capture-phase `change`
 * listener resolves the target BEFORE Horizon's own (bubble-phase) handler on
 * <variant-picker> runs:
 *
 *   - the clicked option keeps its new value;
 *   - every other option, in option order, keeps its value if a variant sells
 *     it with the options already decided, else moves to the available value
 *     nearest to it (nearestSize: W×H area, tie → smaller; a value without a
 *     size, e.g. a Frame colour, falls to the first available in option order).
 *     So Frame is kept when the new Medium sells it (Black on Framed canvas and
 *     Framed poster), else its first colour or "No frame"; Size is kept when
 *     sold, else the nearest.
 *
 * The resolved radios are checked through Horizon's own
 * VariantPicker#updateSelectedOption, so Horizon's handler then builds its
 * request from the full, real combination: exactly one section fetch and one
 * render; the clicked input's data-variant-id is pointed at the resolved
 * variant, so Horizon's ?variant= URL update names it too. When the
 * resolution moves Size, the polite live region under the picker
 * (snippets/geisti-size-note.liquid, rendered empty with its height reserved
 * so Add to cart never moves) names the change and its price — "Now 12×16″ —
 * the smallest Poster, $35.00" — and keeps it until the shopper's next picker
 * interaction.
 *
 * The variant list comes from snippets/geisti-variant-matrix.liquid, rendered
 * once beside the picker only when the product has a Medium option, so stock
 * products are left alone. Hiding values a Medium doesn't sell is CSS
 * (geisti-overrides.css).
 *
 * Loaded as a module on product pages by a layout/theme.liquid patch in
 * scripts/apply-storefront-theme.mjs; test/scripts/variant-resolve.test.ts
 * imports the exports from here.
 */

const area = label => {
  const m = /(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)/.exec(label ?? '');
  return m ? Number(m[1]) * Number(m[2]) : null;
};

export function nearestSize(currentLabel, availableLabels) {
  if (!availableLabels.length) return null;
  const target = area(currentLabel);
  if (target === null) return availableLabels[0];
  let best = null;
  for (const label of availableLabels) {
    const a = area(label);
    if (a === null) continue;
    const d = Math.abs(a - target);
    if (!best || d < best.d || (d === best.d && a < best.a)) best = { label, a, d };
  }
  return best ? best.label : availableLabels[0];
}

// matrix: { options: [{ name, values }], variants: [{ id, options: [value per option] }] }
// selection: { [optionName]: value } with the clicked option's NEW value.
// Returns { selection, available, variantId } — the resolved selection, per
// option the values sold with the options decided before it (in option
// order), and the id of the variant the selection lands on.
export function resolveSelection(matrix, selection, changed) {
  const index = new Map(matrix.options.map((option, i) => [option.name, i]));
  const resolved = { [changed]: selection[changed] };
  const available = {};
  for (const { name, values } of matrix.options) {
    if (name === changed) continue;
    const i = index.get(name);
    const sold = new Set(
      matrix.variants
        .filter(({ options }) =>
          Object.entries(resolved).every(([n, value]) => options[index.get(n)] === value)
        )
        .map(({ options }) => options[i])
    );
    available[name] = values.filter(value => sold.has(value));
    resolved[name] = sold.has(selection[name])
      ? selection[name]
      : nearestSize(selection[name], available[name]);
  }
  const variant = matrix.variants.find(({ options }) =>
    matrix.options.every(({ name }, i) => options[i] === resolved[name])
  );
  return { selection: resolved, available, variantId: variant?.id ?? null };
}

// "Now 12×16″ — the smallest Poster, $35.00" / "… the largest …" / "… the
// closest Poster size, …" / "… the only Poster size, …". `sizes` are the sizes
// the new medium sells; `price` is the landed variant's formatted price.
export function sizeNote(medium, previousSize, newSize, sizes, price) {
  const areas = sizes.map(area);
  const previous = area(previousSize);
  if (!medium || !newSize || previous === null || !areas.length || areas.includes(null)) return '';
  const what =
    sizes.length === 1
      ? `the only ${medium} size`
      : previous < Math.min(...areas)
        ? `the smallest ${medium}`
        : previous > Math.max(...areas)
          ? `the largest ${medium}`
          : `the closest ${medium} size`;
  return `Now ${newSize} — ${what}${price ? `, ${price}` : ''}`;
}

// The matrix renders once beside the picker (outside what Horizon morphs),
// tied to it by product id.
function readMatrix(picker) {
  const script = document.querySelector(
    `script[data-geisti-variant-matrix][data-product-id="${picker.dataset.productId}"]`
  );
  if (!script) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
}

// The slot right after <variant-picker> (outside what Horizon morphs).
const noteFor = picker =>
  picker.nextElementSibling?.matches('[data-geisti-size-note]') ? picker.nextElementSibling : null;

export function resolveBeforeFetch(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
  const picker = input.closest('variant-picker');
  const matrix = picker && readMatrix(picker);
  if (!matrix) return;

  // Any picker interaction retires the previous note.
  const note = noteFor(picker);
  if (note) note.textContent = '';

  const radios = Array.from(picker.querySelectorAll('input[type="radio"][data-fieldset-index]'));
  const changed = matrix.options[Number(input.dataset.fieldsetIndex)]?.name;
  if (!changed) return;

  // Single-value options render no radios; their one value is the selection.
  const selection = Object.fromEntries(
    matrix.options.map((option, i) => {
      const checked = radios.find(r => r.checked && Number(r.dataset.fieldsetIndex) === i);
      return [option.name, checked ? checked.value : option.values[0]];
    })
  );
  selection[changed] = input.value;

  const { selection: target, available, variantId } = resolveSelection(matrix, selection, changed);
  // Horizon writes ?variant= from the clicked input's data-variant-id, which
  // was rendered for the OLD combination; point it at the resolved variant.
  if (variantId) input.dataset.variantId = String(variantId);
  matrix.options.forEach(({ name }, i) => {
    if (name === changed || target[name] === selection[name]) return;
    const radio = radios.find(
      r => Number(r.dataset.fieldsetIndex) === i && r.value === target[name]
    );
    if (!radio) return;
    if (typeof picker.updateSelectedOption === 'function') picker.updateSelectedOption(radio);
    else radio.checked = true;
  });

  if (note && changed !== 'Size' && 'Size' in target && target.Size !== selection.Size) {
    const price = matrix.variants.find(({ id }) => id === variantId)?.price;
    note.textContent = sizeNote(target.Medium, selection.Size, target.Size, available.Size ?? [], price);
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('change', resolveBeforeFetch, true);
}
